import React, {useState, useRef, useEffect} from 'react'
import {Checkmark} from 'hec/assets/icons'

// Components
import {Input} from '@/components/ui/input'
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from '@/components/ui/form'

// Form dependencies and helpers
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'
import {cn} from '@/lib/utils'

type InputWithCtaProps = {
    whiteMode?: boolean
    inputType: 'text' | 'email'
    inputName: string
    label: string
    ctaText: string
    handleSubmit: (data: unknown) => Promise<void>
    className?: string
    isRequired?: boolean
    submitMsg?: string
    errorMsg: string
    attributes?: object
    'data-testid'?: string | undefined
}

export function InputWithCta({
    whiteMode = false,
    inputType = 'text',
    inputName,
    label,
    ctaText,
    handleSubmit,
    className,
    isRequired = false,
    submitMsg,
    errorMsg,
    'data-testid': dataTestId,
    ...props
}: InputWithCtaProps) {
    const [active, setActive] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [isEmpty, setIsEmpty] = useState(true)
    const inputRef = useRef<HTMLInputElement>(null)
    const handleLabelClick = (e: React.MouseEvent) => {
        e.preventDefault()

        if (!active) {
            inputRef?.current?.focus()
        }
    }

    const handleFocus = () => {
        setActive(true)
    }

    const zodObj: {[key: string]: z.ZodString} = {}

    if (isRequired) {
        zodObj[inputName] = z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
    } else {
        zodObj[inputName] = z.string()
    }

    if (inputType === 'email') {
        zodObj[inputName] = zodObj[inputName].email({
            message: errorMsg
        })
    }

    const FormSchema = z.object(zodObj)

    const form = useForm<z.infer<typeof FormSchema>>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            [inputName]: ''
        }
    })

    const handleBlur = () => {
        const input = inputRef.current
        if (!input) return

        setIsEmpty(input.value.length === 0)
        setActive(false)
    }

    const onSubmit = async (data: unknown) => {
        await handleSubmit(data)
        setIsSubmitted(true)
    }

    useEffect(() => {
        const subscription = form.watch(() => {
            if (isSubmitted) setIsSubmitted(false)
        })
        return () => subscription.unsubscribe()
    }, [form, isSubmitted])

    return (
        <Form {...form} data-testid={dataTestId || 'input-with-cta-Form'}>
            <form
                onSubmit={form.handleSubmit(onSubmit)}
                className={cn('flex w-full items-start h-[5.0625rem]', className)}
                {...props}
            >
                <FormField
                    control={form.control}
                    name={inputName}
                    render={({field}) => (
                        <FormItem
                            className="relative grow flex flex-col space-y-0"
                            data-testid={dataTestId || 'input-with-cta-FormItem'}
                        >
                            <FormLabel
                                onClick={handleLabelClick}
                                onMouseDown={(e) => e.preventDefault()}
                                className={cn('absolute px-2.5 top-[0.9375rem] eyebrow-sm z-[1]', {
                                    'top-2 eyebrow-sm': active || !isEmpty,
                                    'eyebrow-common text-xs leading-5 tracking-wide':
                                        !active && isEmpty,
                                    'text-white': whiteMode
                                })}
                                data-testid={dataTestId || 'input-with-cta-FormLabel'}
                            >
                                {label}
                            </FormLabel>
                            {isSubmitted && (
                                <Checkmark className="absolute right-2 top-1/2 w-4 h-4 text-active" />
                            )}
                            <FormControl data-testid={dataTestId || 'input-with-cta-FormControl'}>
                                <Input
                                    {...field}
                                    className={cn(whiteMode && 'text-white border-white')}
                                    autoComplete="off"
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    ref={inputRef}
                                    data-testid={dataTestId || 'input-with-cta-Input'}
                                />
                            </FormControl>
                            <FormMessage
                                className="text-sm text-left px-0"
                                data-testid={dataTestId || 'input-with-cta-FormMessage'}
                            />
                        </FormItem>
                    )}
                    data-testid={dataTestId || 'input-with-cta-FormField'}
                />
                <button
                    className={cn(
                        'flex justify-center items-center h-[3.1875rem] text-btn-secondary-text min-w-[6.75rem] lg:min-w-[9.1875rem] pt-5 px-[0.6875rem] pb-3 border border-solid border-btn-secondary-text rounded-md rounded-b-none font-grotesque font-normal text-base',
                        whiteMode && 'text-white border-white'
                    )}
                    type="submit"
                >
                    {isSubmitted && submitMsg ? submitMsg : ctaText}
                </button>
            </form>
        </Form>
    )
}
